import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFingerprint } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { doc, setDoc } from 'firebase/firestore';
import { getMessaging, getToken } from 'firebase/messaging';
import { Preferences } from '@capacitor/preferences';
import {
  clearCurrentUserProfile,
  getPostVerificationRoute,
  loadUserProfile,
  persistCurrentUserProfile,
  setPendingNotificationRedirect,
} from '../utils/authVerification';
import UsaNetworkMap from '../components/UsaNetworkMap';
import './Login.css';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const isManualAuthFlowRef = useRef(false);

  // Check if biometric login is available (native only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const initBiometric = async () => {
      try {
        const { value: enabled } = await Preferences.get({ key: 'biometricEnabled' });
        if (enabled !== 'true') return;

        const biometricModule = await import('capacitor-native-biometric');
        const NativeBiometric = biometricModule.NativeBiometric || biometricModule.default;
        if (!NativeBiometric || typeof NativeBiometric.isAvailable !== 'function') return;

        const availability = await NativeBiometric.isAvailable({ useFallback: true });
        if (!availability?.isAvailable) return;

        try {
          await NativeBiometric.getCredentials({ server: 'bluewire' });
          setBiometricAvailable(true);
        } catch {
          // No stored credentials yet; button will stay hidden
        }
      } catch (err) {
        console.warn('Biometric availability check failed:', err);
      }
    };

    initBiometric();
  }, []);

  const queuePendingNotificationRedirect = (user) => {
    const redirectTo = location.state?.redirectTo;
    if (redirectTo && user?.uid) {
      setPendingNotificationRedirect(redirectTo, user.uid);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user || isManualAuthFlowRef.current) return;

      const canEnterApp = await routeSignedInUser(user);
      if (canEnterApp) {
        navigate('/splash', { replace: true });
      }
    });

    return () => unsubscribe();
  }, []);

  const routeSignedInUser = async (user) => {
    await user.reload();
    if (!user.emailVerified) {
      clearCurrentUserProfile();
      toast.info('Please verify your email to continue.');
      localStorage.setItem('pendingEmail', user.email || '');
      navigate('/verify-email');
      return false;
    }

    const profileData = await loadUserProfile(user.uid);
    if (!profileData) {
      clearCurrentUserProfile();
      toast.error('User profile not found.');
      return false;
    }

    const nextRoute = getPostVerificationRoute(user, profileData);

    if (nextRoute === '/verify-phone') {
      clearCurrentUserProfile();
      toast.info('Please verify your phone number to continue.');
      navigate('/verify-phone');
      return false;
    }

    persistCurrentUserProfile(user.uid, profileData);

    if (nextRoute === '/my-account') {
      saveFcmToken(user);
      toast.warn('Your account is suspended. Please renew your subscription.');
      navigate('/my-account');
      return false;
    }

    if (nextRoute === '/verify-id') {
      toast.info('Please complete ID verification to continue.');
      navigate('/verify-id');
      return false;
    }

    saveFcmToken(user);

    sessionStorage.setItem(
      'pendingWelcomeMessage',
      `Welcome ${profileData.firstInitialLastName || user.email}`
    );
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter both email and password.');
      return;
    }

    try {
      isManualAuthFlowRef.current = true;
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      queuePendingNotificationRedirect(user);
      console.log('ƒo. Firebase login successful:', user.email);

      // If biometric unlock is enabled, store credentials securely on device (native only)
      if (Capacitor.isNativePlatform()) {
        try {
          const { value: enabled } = await Preferences.get({ key: 'biometricEnabled' });
          if (enabled === 'true') {
            const biometricModule = await import('capacitor-native-biometric');
            const NativeBiometric = biometricModule.NativeBiometric || biometricModule.default;
            if (NativeBiometric && typeof NativeBiometric.isAvailable === 'function') {
              const availability = await NativeBiometric.isAvailable({ useFallback: true });
              if (availability?.isAvailable && typeof NativeBiometric.setCredentials === 'function') {
                await NativeBiometric.setCredentials({
                  username: email,
                  password,
                  server: 'bluewire',
                });
                console.log('Stored credentials for biometric login.');
              }
            }
          }
        } catch (credErr) {
          console.warn('Failed to store biometric credentials:', credErr);
        }
      }

      const canEnterApp = await routeSignedInUser(user);
      if (!canEnterApp) {
        return;
      }

      console.log('ƒo. Showing warning modal before navigating...');
      navigate('/splash', { replace: true });
    } catch (error) {
      console.error('ƒ?O Firebase login error:', error.message);
      if (
        error?.code === 'auth/invalid-credential' ||
        error?.code === 'auth/user-not-found' ||
        error?.code === 'auth/wrong-password' ||
        error?.code === 'auth/invalid-login-credentials'
      ) {
        toast.error('The information entered does not match our records.');
      } else {
        toast.error(error.message);
      }
    } finally {
      isManualAuthFlowRef.current = false;
    }
  };

  const saveFcmToken = async (user) => {
    try {
      // Skip Firebase Web Messaging inside mobile (Capacitor)
      if (Capacitor.getPlatform() !== 'web') {
        console.log("dYs® Skipping web-based FCM token on mobile.");
        return;
      }

      // Desktop web version uses Firebase Web Messaging
      const messaging = getMessaging();
      const token = await getToken(messaging, {
        vapidKey: 'BLrCz-o9rv6jyXRMh2WVro5TH62x2FKgMFHaGAXcJDN-IYucZKU5MnYu7MA1a6r5OCgzqsi3lQJHT-vi_mdbCfA',
      });

      console.log('dYO? Web FCM Token:', token);

      await setDoc(doc(db, 'users', user.uid), { pushToken: token, fcmToken: token }, { merge: true });

    } catch (err) {
      console.error('Failed to get/save FCM token:', err);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address first.');
      return;
    }

    const confirmReset = window.confirm(
      `We will send a password reset email to: ${email}\n\n` +
      `Make sure to also check your spam/junk folder as it may be delivered there.\n\n` +
      `Do you want to continue?`
    );

    if (!confirmReset) {
      return; // User cancelled
    }

    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent. Please check your inbox (and spam folder).');
    } catch (error) {
      console.error('ƒ?O Password reset error:', error.message);
      toast.error(error.message);
    }
  };


  const handleBiometricLogin = async () => {
    if (!Capacitor.isNativePlatform()) return;

    try {
      isManualAuthFlowRef.current = true;
      const biometricModule = await import('capacitor-native-biometric');
      const NativeBiometric = biometricModule.NativeBiometric || biometricModule.default;
      if (!NativeBiometric) return;

      const availability = await NativeBiometric.isAvailable({ useFallback: true });
      if (!availability?.isAvailable) {
        toast.info('Biometric authentication is not available on this device.');
        return;
      }

      await NativeBiometric.verifyIdentity({
        reason: 'Login to BlueWire',
      });

      const creds = await NativeBiometric.getCredentials({ server: 'bluewire' });
      if (!creds?.username || !creds?.password) {
        toast.error('No saved credentials found for biometric login.');
        return;
      }

      const userCredential = await signInWithEmailAndPassword(auth, creds.username, creds.password);
      const user = userCredential.user;
      queuePendingNotificationRedirect(user);
      console.log('ƒo. Firebase biometric login successful:', user.email);

      const canEnterApp = await routeSignedInUser(user);
      if (!canEnterApp) {
        return;
      }

      console.log('ƒo. Navigating to splash (biometric login)...');
      navigate('/splash', { replace: true });
    } catch (error) {
      console.error('Biometric login error:', error);
      toast.error('Biometric login failed. You can still sign in with email and password.');
    } finally {
      isManualAuthFlowRef.current = false;
    }
  };

  return (
    <div className="login-page">
      {/* Left side - Blue background with barbed wire as the "L" */}
      <div className="w-full md:w-1/2 bg-blue-900 text-white flex flex-col justify-center items-center p-10 relative">
        <UsaNetworkMap className="login-usa-map" />
        <img
          src="/bluewire-messaging-logo.png"
          onError={(event) => { event.currentTarget.src = '/logo512.png'; }}
          alt="BlueWire"
          className="login-brand-logo"
        />
        <div className="login-mantra" aria-label="Connect, Communicate, Collaborate">
          <span>Connect</span>
          <span aria-hidden="true">.</span>
          <span>Communicate</span>
          <span aria-hidden="true">.</span>
          <span>Collaborate</span>
        </div>

        {/* Bullet points */}
        <div className="relative z-10 text-left">
          <p className="flex items-start gap-2 ml-10 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg"
                 fill="currentColor"
                 viewBox="0 0 24 24"
                 className="w-5 h-5 text-white flex-shrink-0 mt-1">
              <path
                fillRule="evenodd"
                d="M12 2.25c-.41 0-.82.09-1.19.27L4.5 5.25v6c0 5.07 3.57 
                  9.53 8.43 10.61.35.07.72.07 1.07 0 4.86-1.08 
                  8.43-5.54 8.43-10.61v-6l-6.31-2.73c-.37-.18-.78-.27-1.19-.27H12zm3.72 
                  8.47a.75.75 0 00-1.06-1.06l-4.19 4.19-2.19-2.2a.75.75 
                  0 00-1.06 1.06l2.72 2.72c.3.3.77.3 1.06 
                  0l4.72-4.71z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Peer-to-peer platform for law enforcement professionals.
            </span>
          </p>

          <p className="flex items-start gap-2 ml-10 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg"
                 fill="currentColor"
                 viewBox="0 0 24 24"
                 className="w-5 h-5 text-white flex-shrink-0 mt-1">
              <path
                fillRule="evenodd"
                d="M12 2.25c-.41 0-.82.09-1.19.27L4.5 5.25v6c0 5.07 3.57 
                  9.53 8.43 10.61.35.07.72.07 1.07 0 4.86-1.08 
                  8.43-5.54 8.43-10.61v-6l-6.31-2.73c-.37-.18-.78-.27-1.19-.27H12zm3.72 
                  8.47a.75.75 0 00-1.06-1.06l-4.19 4.19-2.19-2.2a.75.75 
                  0 00-1.06 1.06l2.72 2.72c.3.3.77.3 1.06 
                  0l4.72-4.71z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Connect with officers you don't know but need to reach. 
            </span>
          </p>

          <p className="flex items-start gap-2 ml-10 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg"
                 fill="currentColor"
                 viewBox="0 0 24 24"
                 className="w-5 h-5 text-white flex-shrink-0 mt-1">
              <path
                fillRule="evenodd"
                d="M12 2.25c-.41 0-.82.09-1.19.27L4.5 5.25v6c0 5.07 3.57 
                  9.53 8.43 10.61.35.07.72.07 1.07 0 4.86-1.08 
                  8.43-5.54 8.43-10.61v-6l-6.31-2.73c-.37-.18-.78-.27-1.19-.27H12zm3.72 
                  8.47a.75.75 0 00-1.06-1.06l-4.19 4.19-2.19-2.2a.75.75 
                  0 00-1.06 1.06l2.72 2.72c.3.3.77.3 1.06 
                  0l4.72-4.71z"
                clipRule="evenodd"
              />
            </svg>
	    <span>
              Communicate with LE professionals from other units and agencies.
            </span>
          </p>

          <p className="flex items-start gap-2 ml-10 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg"
                 fill="currentColor"
                 viewBox="0 0 24 24"
                 className="w-5 h-5 text-white flex-shrink-0 mt-1">
              <path
                fillRule="evenodd"
                d="M12 2.25c-.41 0-.82.09-1.19.27L4.5 5.25v6c0 5.07 3.57 
                  9.53 8.43 10.61.35.07.72.07 1.07 0 4.86-1.08 
                  8.43-5.54 8.43-10.61v-6l-6.31-2.73c-.37-.18-.78-.27-1.19-.27H12zm3.72 
                  8.47a.75.75 0 00-1.06-1.06l-4.19 4.19-2.19-2.2a.75.75 
                  0 00-1.06 1.06l2.72 2.72c.3.3.77.3 1.06 
                  0l4.72-4.71z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Collaborate on cases and developing situations in real time.
            </span>
          </p>

          <p className="flex items-start gap-2 ml-10 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg"
                 fill="currentColor"
                 viewBox="0 0 24 24"
                 className="w-5 h-5 text-white flex-shrink-0 mt-1">
              <path
                fillRule="evenodd"
                d="M12 2.25c-.41 0-.82.09-1.19.27L4.5 5.25v6c0 5.07 3.57 
                  9.53 8.43 10.61.35.07.72.07 1.07 0 4.86-1.08 
                  8.43-5.54 8.43-10.61v-6l-6.31-2.73c-.37-.18-.78-.27-1.19-.27H12zm3.72 
                  8.47a.75.75 0 00-1.06-1.06l-4.19 4.19-2.19-2.2a.75.75 
                  0 00-1.06 1.06l2.72 2.72c.3.3.77.3 1.06 
                  0l4.72-4.71z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Build your network.
            </span>
          </p>

          <p className="flex items-start gap-2 ml-10 mb-4 login-download-bullet">
            <svg xmlns="http://www.w3.org/2000/svg"
                 fill="currentColor"
                 viewBox="0 0 24 24"
                 className="w-5 h-5 text-white flex-shrink-0 mt-1">
              <path
                fillRule="evenodd"
                d="M12 2.25c-.41 0-.82.09-1.19.27L4.5 5.25v6c0 5.07 3.57
                  9.53 8.43 10.61.35.07.72.07 1.07 0 4.86-1.08
                  8.43-5.54 8.43-10.61v-6l-6.31-2.73c-.37-.18-.78-.27-1.19-.27H12zm3.72
                  8.47a.75.75 0 00-1.06-1.06l-4.19 4.19-2.19-2.2a.75.75
                  0 00-1.06 1.06l2.72 2.72c.3.3.77.3 1.06
                  0l4.72-4.71z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              {' '}
              <button
                type="button"
                className="login-download-link"
                onClick={() => navigate('/download')}
              >
                Get the BlueWire app
              </button>
            </span>
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex flex-col justify-center items-center w-full md:w-1/2 bg-gray-50 p-6">
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
          <h2 className="text-2xl font-semibold text-center mb-6">
            Login to Your Account
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="login-field login-field--email">
              <label className="block text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="login-field login-field--password">
              <label className="block text-gray-700 mb-1">Password</label>
              <input
                type="password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <div className="text-right mt-1">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md transition duration-200"
            >
              Login
            </button>

            {biometricAvailable && (
              <button
  type="button"
  onClick={handleBiometricLogin}
  className="w-full mt-2 bg-blue-50 text-blue-700 border border-blue-300 py-2 rounded-md font-medium hover:bg-blue-100 transition duration-200"
 >
  <FontAwesomeIcon icon={faFingerprint} className="mr-2" />
  Login with biometrics
</button>
            )}
          </form>

          <div className="mt-4 text-center">
            <span className="text-gray-600">Don't have an account? </span>
            <button
              onClick={() => navigate('/register')}
              className="text-blue-600 hover:underline font-medium"
            >
              Register
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />

    </div>
  );
}
